/**
 * SceneManager: owns the lifecycle of one scene's content — the gsplat entity
 * + asset, the collision object, and the hotspot/zone/portal managers.
 *
 * Scene *switching* (fade, unload, load, player reposition) is orchestrated by
 * main.ts; this class only loads/unloads content and exposes the current
 * instance so the update loop can drive the managers.
 *
 * Unloading destroys the gsplat entity and calls asset.unload() + assets.remove()
 * so the engine frees the streamed-SOG textures (WebP → VRAM), letting large
 * scenes be swapped without leaking memory.
 */

import type { Application } from 'playcanvas';
import type { Collision } from '../collision';
import type { SceneManifest, SceneSkyboxConfig } from '../config/manifest';
import type { SceneObject } from './SceneObjectTypes';
import { isMobileRuntime } from '../app/createApp';
import { eventBus } from '../events/EventBus';
import { loadGaussianScene } from './loadGaussianScene';
import { loadCollision } from './loadCollision';
import { OcclusionMeshSystem } from './OcclusionMeshSystem';
import { SkyboxSystem } from './SkyboxSystem';
import { HotspotManager } from './hotspotManager';
import { TriggerZoneManager } from './triggerZoneManager';
import { PortalManager } from './portalManager';
import { SceneObjectManager } from './SceneObjectManager';

export interface SceneInstance {
    manifest: SceneManifest;
    /** gsplat entity (null if the splat failed to load). */
    entity: import('playcanvas').Entity | null;
    /** gsplat asset handle for unload (null if load failed). */
    asset: import('playcanvas').Asset | null;
    /** Collision object (null if unavailable → free-flight). */
    collision: Collision | null;
    /** Invisible depth occluder system (null only if it failed to construct). */
    occlusion: OcclusionMeshSystem;
    /** Per-scene skybox system. */
    skybox: SkyboxSystem;
    hotspots: HotspotManager;
    zones: TriggerZoneManager;
    portals: PortalManager;
    /** Unified object manager (objects[] or converted legacy arrays). */
    objects: SceneObjectManager;
    deferredAssetsStarted?: boolean;
}

export function resolveSceneSplatUrl(manifest: SceneManifest): string {
    return isMobileRuntime() && manifest.mobileSplatUrl
        ? manifest.mobileSplatUrl
        : manifest.splatUrl;
}

export function resolveSceneSkyboxConfig(manifest: SceneManifest, mobileRuntime: boolean): SceneSkyboxConfig | undefined {
    return mobileRuntime ? (manifest.mobileSkybox ?? manifest.skybox) : manifest.skybox;
}

function nowMs(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

class SceneManager {
    private _app: Application;
    current: SceneInstance | null = null;
    /** Reference count of active hideScene trigger zones (0 = content visible). */
    private _hiddenCount = 0;

    constructor(app: Application) {
        this._app = app;
    }

    /**
     * Load a scene's content from its manifest. Non-fatal: a failed splat or
     * collision leaves that part null so the app can still run.
     */
    async loadScene(manifest: SceneManifest): Promise<SceneInstance> {
        const sceneLoadStartedAt = nowMs();
        const mobileRuntime = isMobileRuntime();
        const logStage = (stage: string, progress: number, extra = '') => {
            const elapsed = ((nowMs() - sceneLoadStartedAt) / 1000).toFixed(2);
            console.info(`[scene-load] ${manifest.id} ${stage} ${elapsed}s progress=${Math.round(progress * 100)}%${extra ? ` ${extra}` : ''}`);
        };
        const markStage = (stage: string, progress: number, extra = '') => {
            logStage(stage, progress, extra);
            eventBus.emit({ type: 'SCENE_LOAD_START', name: manifest.name, progress });
        };
        markStage('start', 0, `mobile=${mobileRuntime} splat=${resolveSceneSplatUrl(manifest)}`);

        // --- Gaussian splat (non-fatal) ---
        let entity: SceneInstance['entity'] = null;
        let asset: SceneInstance['asset'] = null;
        markStage('gsplat:start', 0.05);
        try {
            const handle = await loadGaussianScene(
                this._app,
                resolveSceneSplatUrl(manifest),
                (frac) => {
                    eventBus.emit({ type: 'SCENE_LOAD_START', name: manifest.name, progress: frac });
                },
                { mobileLowQuality: mobileRuntime }
            );
            entity = handle.entity;
            asset = handle.asset;
            eventBus.emit({ type: 'LOAD_SUCCESS' });
            markStage('gsplat:done', 0.2, `asset=${asset.name}`);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            markStage('gsplat:error', 0.2, msg);
            eventBus.emit({
                type: 'LOAD_ERROR',
                message: `高斯场景「${manifest.name}」加载失败：${msg}`
            });
        }

        // --- Skybox (non-fatal) ---
        const skybox = new SkyboxSystem(this._app);
        markStage('skybox:start', 0.25);
        if (mobileRuntime) {
            markStage('skybox:deferred', 0.35);
            markStage('skybox:done', 0.35);
        } else {
            try {
                await skybox.load(resolveSceneSkyboxConfig(manifest, mobileRuntime));
                markStage('skybox:done', 0.35);
            } catch (err) {
                console.warn('[skybox] load failed:', err);
                markStage('skybox:done', 0.35, 'failed');
            }
        }

        // --- Collision (non-fatal) ---
        // 碰撞一律后装（原手机 defer 扩展到桌面，2026-09-26）：collision GLB
        // （laomendong2 15.7MB + 87 万三角形 BVH 构建）是进场加载的大头，挡在
        // SCENE_READY 前会把整个下载+构建时长加进 loading。进场先无碰撞（飞
        // 模式），由 loadDeferredAssets 后装、installSceneCollision 迟到升级
        // （walk 默认场景自动切行走，见 main.ts）。
        let collision: Collision | null = null;
        markStage('collision:deferred', 0.55, manifest.collisionUrl ?? 'none');
        markStage('collision:done', 0.55, 'deferred');

        // --- Occlusion mesh (non-fatal; defaults to the collision GLB) ---
        // 同碰撞一并后装：遮挡体 URL 常与 collision GLB 同文件（laomendong2），
        // 桌面原先 await 它等于把同一大文件再拖一道进 loading。load() 失败不抛
        // 错而是 resolve null（非致命设计）。
        const occlusion = new OcclusionMeshSystem(this._app);
        const occlusionUrl = manifest.occlusionUrl ?? manifest.collisionUrl;
        markStage('occlusion:deferred', 0.75, occlusionUrl ?? 'none');
        markStage('occlusion:done', 0.75, 'deferred');

        // Partition trigger sources to avoid double-firing: if the manifest
        // declares `objects`, the unified SceneObjectManager owns all triggers
        // and the legacy managers get empty arrays. Otherwise legacy arrays own
        // triggers (preserving existing click-to-open behavior) and the objects
        // manager is empty. Editor-added objects always go to the objects manager.
        const hasObjects = Array.isArray(manifest.objects);
        const hotspots = new HotspotManager(this._app, hasObjects ? [] : manifest.hotspots);
        const zones = new TriggerZoneManager(hasObjects ? [] : manifest.triggerZones);
        const portals = new PortalManager(this._app, hasObjects ? [] : manifest.portals);
        const objects = new SceneObjectManager(
            this._app,
            hasObjects ? (manifest.objects as SceneObject[]) : [],
            collision,
            { deferMediaAssets: mobileRuntime }
        );
        if (mobileRuntime) markStage('objects:deferred', 0.85, `media=${objects.deferredMediaCount}`);
        // 碰撞后装：初始恒为 null，loadDeferredAssets 到货后回填。
        const initialCollision = null;
        markStage('objects:done', 0.85, `objects=${hasObjects ? (manifest.objects as SceneObject[]).length : 0}`);

        const instance: SceneInstance = { manifest, entity, asset, collision: initialCollision, occlusion, skybox, hotspots, zones, portals, objects };
        this.current = instance;

        // Note: SCENE_READY is NOT emitted here — main.ts emits it after the
        // spawn-area gsplat preload completes, so the load bar reflects real
        // chunk loading and stays visible until the first view is ready.
        return instance;
    }

    /** 进场后延迟装载体（桌面与手机统一入口）：碰撞/遮挡体两平台都后装；天空盒与媒体资产仅手机 defer（桌面 loadScene 已同步装）。 */
    loadDeferredAssets(instance: SceneInstance, onCollisionReady?: (collision: Collision | null) => void): void {
        if (instance.deferredAssetsStarted) return;
        instance.deferredAssetsStarted = true;
        const mobile = isMobileRuntime();
        const startedAt = nowMs();
        const logStage = (stage: string, progress: number, extra = '') => {
            const elapsed = ((nowMs() - startedAt) / 1000).toFixed(2);
            console.info(`[scene-load] ${instance.manifest.id} ${stage} ${elapsed}s progress=${Math.round(progress * 100)}%${extra ? ` ${extra}` : ''}`);
        };
        logStage('deferred:start', 0.86);
        const queueDeferredTask = (label: string, delayMs: number, task: () => Promise<void> | void): Promise<void> => {
            const queuedAt = nowMs();
            logStage(`${label}:background:queued`, 0.86, `delay=${delayMs}ms`);
            return new Promise<void>((done) => {
                window.setTimeout(() => {
                    if (this.current !== instance) { done(); return; }
                    const runStartedAt = nowMs();
                    logStage(`${label}:background:run`, 0.86, `actualDelay=${Math.round(runStartedAt - queuedAt)}ms`);
                    void Promise.resolve(task())
                        .then(() => {
                            if (this.current !== instance) { done(); return; }
                            logStage(`${label}:background:task:done`, 0.86, `taskMs=${Math.round(nowMs() - runStartedAt)}ms`);
                        })
                        .catch((err) => {
                            if (this.current !== instance) { done(); return; }
                            console.warn(`[scene-load] ${label} failed:`, err);
                            logStage(
                                `${label}:background:failed`,
                                0.86,
                                `${err instanceof Error ? err.message : String(err)} taskMs=${Math.round(nowMs() - runStartedAt)}ms`
                            );
                        })
                        .finally(done);
                }, delayMs);
            });
        };

        let collisionFailed = false;
        const collisionTask = queueDeferredTask('collision', 0, async () => {
            logStage('collision:background:start', 0.55, instance.manifest.collisionUrl ?? 'none');
            if (!instance.manifest.collisionUrl) { onCollisionReady?.(null); return; }
            let failed = false;
            let baseCollision: Collision | null = null;
            try {
                baseCollision = await loadCollision(this._app, instance.manifest.collisionUrl);
            } catch (err) {
                // 下载/解析失败不让任务异常冒泡：onCollisionReady 必须照常回调，
                // 调用方（main.ts）的碰撞优先级门靠它放行精细 LOD 预载。
                failed = true;
                collisionFailed = true;
                console.warn('[scene-load] collision deferred load failed:', err);
            }
            if (this.current !== instance) return;
            if (!failed) {
                instance.objects.setBaseCollision(baseCollision);
                instance.collision = baseCollision ? instance.objects.collision : null;
            }
            onCollisionReady?.(instance.collision);
            logStage('collision:background:done', 0.55, failed ? 'failed' : baseCollision ? 'available' : 'none');
        });

        queueDeferredTask('occlusion', 120, async () => {
            const occlusionUrl = instance.manifest.occlusionUrl ?? instance.manifest.collisionUrl;
            logStage('occlusion:background:start', 0.75, occlusionUrl ?? 'none');
            let handle: Awaited<ReturnType<OcclusionMeshSystem['load']>> = null;
            if (occlusionUrl) {
                if (occlusionUrl === instance.manifest.collisionUrl) {
                    // 同一文件（laomendong2 显式同指，meihuashan 等回退 collision）：
                    // 等碰撞任务结束再经注册表共享同一资产，绝不并行二次下载；
                    // 碰撞已失败则直接跳过——遮挡体自带 5 级重试梯，否则会把
                    // 刚失败的十几个 MB 原样再拖一遍。
                    await collisionTask;
                    if (this.current !== instance) return;
                    if (collisionFailed) {
                        logStage('occlusion:background:done', 0.75, 'skipped — collision failed (same file)');
                        return;
                    }
                }
                handle = await instance.occlusion.load(occlusionUrl);
            }
            if (this.current !== instance) return;
            logStage('occlusion:background:done', 0.75, occlusionUrl ? (handle ? 'available' : 'unavailable') : 'none');
        });

        // 天空盒/媒体 defer 是手机专属（桌面 loadScene 已同步装好）。
        if (mobile) queueDeferredTask('skybox', 240, async () => {
            logStage('skybox:background:start', 0.35);
            const skybox = instance.skybox;
            await skybox.load(resolveSceneSkyboxConfig(instance.manifest, true));
            if (this.current !== instance) return;
            logStage('skybox:background:done', 0.35);
        });

        if (mobile) queueDeferredTask('objects:sprite', 360, () => {
            const mediaCount = instance.objects.loadDeferredMediaAssets(['sprite']);
            logStage('objects:sprite:background:done', 0.85, `media=${mediaCount}`);
        });

        if (mobile) queueDeferredTask('objects:video', 520, () => {
            const mediaCount = instance.objects.loadDeferredMediaAssets(['video']);
            logStage('objects:video:background:done', 0.85, `media=${mediaCount}`);
        });

        if (mobile) queueDeferredTask('objects:model', 760, () => {
            const mediaCount = instance.objects.loadDeferredMediaAssets(['glb', 'gsplat']);
            logStage('objects:model:background:done', 0.85, `media=${mediaCount}`);
        });

        logStage('deferred:queued', 0.86, `media=${instance.objects.deferredMediaCount}`);
    }

    /**
     * Unload a scene's content: destroy managers, the gsplat entity, and unload
     * the gsplat asset so its textures are freed.
     */
    unloadScene(instance: SceneInstance) {
        instance.hotspots.destroy();
        instance.zones.destroy();
        instance.portals.destroy();
        instance.objects.destroy();
        instance.occlusion.destroy();
        instance.skybox.destroy();

        if (instance.entity) {
            this._app.root.removeChild(instance.entity);
            instance.entity.destroy();
        }
        if (instance.asset) {
            this._app.assets.remove(instance.asset);
            instance.asset.unload();
        }
        if (this.current === instance) this.current = null;
        this._hiddenCount = 0;
        eventBus.emit({ type: 'SCENE_UNLOADED', name: instance.manifest.name });
    }

    /**
     * Hide all scene content (gsplat + non-collider object markers) while
     * keeping the skybox, the scene's collision/occlusion GLB, and editor
     * collider objects visible. Uses a reference count so overlapping
     * hideScene zones restore only when the last one is exited.
     */
    hideSceneContent(): void {
        this._hiddenCount++;
        if (this._hiddenCount === 1) this._applyContentHidden(true);
    }

    /** Decrement the hide count; restores content when it reaches zero. */
    restoreSceneContent(): void {
        if (this._hiddenCount <= 0) return;
        this._hiddenCount--;
        if (this._hiddenCount === 0) this._applyContentHidden(false);
    }

    /** Whether scene content is currently hidden by a hideScene zone. */
    get contentHidden(): boolean { return this._hiddenCount > 0; }

    private _applyContentHidden(hidden: boolean): void {
        const inst = this.current;
        if (!inst) return;
        // Gaussian splat entity.
        if (inst.entity) inst.entity.enabled = !hidden;
        // All object markers except editor colliders (colliders stay visible).
        for (const o of inst.objects.objects) {
            if (o.type === 'collider') continue;
            const m = inst.objects.getMarkerEntity(o.id);
            if (m) m.enabled = !hidden;
        }
        // Skybox, occlusion mesh and collision are intentionally untouched:
        // the skybox is owned by SkyboxSystem, the occlusion/collision GLB
        // stay visible so the player still sees the scene shell.
    }

    /** Approximate live-asset count — used by the memory-verification log. */
    assetCount(): number {
        return this._app.assets.list().length;
    }
}

export { SceneManager };
